import React, { useState, useCallback } from 'react';
import { Users, X, Shuffle, Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const TeamMode = ({ entries, onEntriesChange }) => {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [numTeams, setNumTeams] = useState(2);
  const [teams, setTeams] = useState([]);
  const [teamNames, setTeamNames] = useState([`${t('teamMode.team') || 'Team'} 1`, `${t('teamMode.team') || 'Team'} 2`]);

  // Generate teams from entries
  const generateTeams = useCallback(() => {
    const enabledEntries = entries.filter(e => e.enabled !== false);
    if (enabledEntries.length < numTeams) return;

    // Shuffle entries using crypto random
    const shuffled = [...enabledEntries];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const randomValues = new Uint32Array(1);
      crypto.getRandomValues(randomValues);
      const j = randomValues[0] % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    // Distribute to teams
    const newTeams = Array.from({ length: numTeams }, () => []);
    shuffled.forEach((entry, index) => {
      newTeams[index % numTeams].push(entry);
    });

    setTeams(newTeams);
  }, [entries, numTeams]);

  // Update team count
  const handleTeamCountChange = (count) => {
    const newCount = Math.max(2, Math.min(10, count));
    setNumTeams(newCount);
    
    // Expand teamNames if needed
    if (newCount > teamNames.length) {
      const newNames = [...teamNames];
      for (let i = teamNames.length; i < newCount; i++) {
        newNames.push(`${t('teamMode.team') || 'Team'} ${i + 1}`);
      }
      setTeamNames(newNames);
    }
    setTeams([]);
  };

  // Update team name
  const handleTeamNameChange = (index, name) => {
    const newNames = [...teamNames];
    newNames[index] = name;
    setTeamNames(newNames);
  };

  // Copy teams to clipboard
  const copyTeams = async () => {
    if (teams.length === 0) return;
    
    const text = teams.map((team, i) => 
      `${teamNames[i] || `${t('teamMode.team') || 'Team'} ${i + 1}`}:\n${team.map(e => `  - ${e.text}`).join('\n')}`
    ).join('\n\n');

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const enabledCount = entries.filter(e => e.enabled !== false).length;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="btn-action-bar"
        aria-label={t('wheel.teams')}
        title={t('teamMode.title')}
        data-testid="team-mode-button"
        disabled={enabledCount < 2}
      >
        <Users size={20} />
        <span>{t('wheel.teams')}</span>
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal team-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <h2>👥 {t('teamMode.title')}</h2>

            {/* Team count selector */}
            <div className="team-count-selector">
              <label>{t('teamMode.numberOfTeams')}:</label>
              <div className="team-count-controls">
                <button 
                  onClick={() => handleTeamCountChange(numTeams - 1)}
                  disabled={numTeams <= 2}
                >
                  −
                </button>
                <span className="team-count-value">{numTeams}</span>
                <button 
                  onClick={() => handleTeamCountChange(numTeams + 1)}
                  disabled={numTeams >= 10 || numTeams >= enabledCount}
                >
                  +
                </button>
              </div>
            </div>

            {/* Generate button */}
            <button 
              onClick={generateTeams} 
              className="btn-generate-teams"
              disabled={enabledCount < numTeams}
            >
              <Shuffle size={16} />
              {t('wheel.generateTeams')}
            </button>

            {/* Teams display */}
            {teams.length > 0 && (
              <div className="teams-container">
                {teams.map((team, i) => (
                  <div key={i} className="team-card">
                    <input
                      type="text"
                      value={teamNames[i] || `${t('teamMode.team') || 'Team'} ${i + 1}`}
                      onChange={(e) => handleTeamNameChange(i, e.target.value)}
                      className="team-name-input"
                      maxLength={30}
                    />
                    <ul className="team-members">
                      {team.map((member, j) => (
                        <li key={j} className="team-member">
                          <span 
                            className="member-color" 
                            style={{ backgroundColor: member.color }}
                          />
                          {member.text}
                        </li>
                      ))}
                    </ul>
                    <span className="team-count">{team.length}</span>
                  </div>
                ))}

                <button onClick={copyTeams} className="btn-copy-teams">
                  {t('wheel.copyResults')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TeamMode;
